import { useEffect } from "react";
import { Instagram } from "lucide-react";
import { Grain } from "@/components/site/primitives";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const PAGE_PATH = "/blog/jailer-2-action-director-chethan-dsouza";
const TITLE = "Jailer 2 Action Director Chethan D’Souza: Journey, Films & Career";
const DESCRIPTION =
  "Meet Chethan D’Souza, the action director behind Rajinikanth’s Jailer 2 — his journey from a karate class in Udupi to Kannada cinema, Aavesham and motion capture with CF Action Design.";
const PUBLISH_DATE = "2026-10-15";
const INSTAGRAM_URL = "https://www.instagram.com/chethandsouza";

const SECTIONS = [
  {
    heading: "Who Is Chethan D’Souza, the Jailer 2 Action Director?",
    paragraphs: [
      "Chethan D’Souza is an Indian action director, stunt choreographer and motion capture director with more than 22 years of experience in martial arts and movement arts. He is the founder and lead action designer of CF Action Design and the co-founder of Chaos Faktory, a stunt training centre in Bengaluru.",
      "His work involves developing fight choreography, planning stunts, designing action sequences and collaborating with directors, actors, cinematographers and stunt teams. His approach combines physical performance with visual storytelling, helping action sequences support the characters and emotions of a film.",
    ],
  },
  {
    heading: "From Udupi to Bengaluru: His Early Life and Martial Arts Journey",
    paragraphs: [
      "Chethan’s interest in martial arts began when he was studying in school. During a shopping trip with his mother in Udupi, he noticed children practising karate in white uniforms. He wanted to learn the discipline, but there was no nearby class available at the time.",
      "When his school introduced karate classes in the eighth standard, he became the first student to join.",
      "He continued his education at MGM College in Udupi, pursuing a Bachelor of Science degree while teaching karate classes in his hometown of Moodubelle. His younger brothers were among his early students.",
      "By the time he reached the 12th standard, Chethan had earned his black belt. He subsequently competed at state, national and international levels and became a two-time state-level grand champion.",
      "Over the years, he developed expertise in multiple martial arts and weapon systems. He also taught himself parkour, gymnastics and calisthenics, expanding his understanding of movement, balance, coordination and physical performance.",
    ],
  },
  {
    heading: "From Software Engineer to Full-Time Action Director",
    paragraphs: [
      "In 2007, Chethan moved to Bengaluru to work as a software developer at Accenture. Although he had a corporate career, his interest in martial arts and action filmmaking continued to grow.",
      "Outside office hours, he practised parkour and gymnastics and experimented with combining these movements with martial arts for the camera. He spent close to a year developing his ideas and learning how to present physical movement as cinematic action.",
      "His efforts resulted in a parkour short film titled “Wrong Move”, which attracted close to ten lakh views on YouTube.",
      "The project became an important turning point in his career. Without established film industry connections, Chethan shared the short film with director Pawan Kumar through Facebook.",
      "Pawan Kumar responded within hours and invited him to meet the following day. After discussing the action sequence required for “U Turn”, Chethan created and presented the fight sequence within a week.",
      "This opportunity led to his involvement in the Kannada film “U Turn” as a fight choreographer.",
      "After approximately 12 years in corporate life, he eventually left software development to concentrate on action direction.",
    ],
  },
  {
    heading: "Chethan D’Souza’s Career in Kannada Cinema",
    paragraphs: [
      "Kannada cinema has played a significant role in Chethan’s development as an action director. His work brought him opportunities to collaborate with established actors and filmmakers while building his experience in stunt choreography and action design.",
      "Chethan has credited the late Puneeth Rajkumar with helping him obtain his stunt union card and take an important step towards becoming a stunt director.",
      "Over the course of his career, he has worked on films associated with actors including Shiva Rajkumar, Puneeth Rajkumar, Sudeep, Upendra, Sriimurali and Raj B. Shetty.",
    ],
    subheading: "His Kannada film credits include:",
    list: ["U Turn", "Bagheera", "UI", "Max", "45", "Bhairathi Ranagal"],
    paragraphsAfterList: [
      "These projects represent different styles of storytelling and action, giving him opportunities to work on sequences that require careful choreography, physical preparation and coordination with the wider filmmaking team.",
      "His experience in Kannada cinema also helped establish the professional foundation for his work in other Indian film industries.",
    ],
  },
  {
    heading: "Aavesham: A Major Breakthrough Beyond Kannada Cinema",
    paragraphs: [
      "The 2024 Malayalam film “Aavesham” became an important milestone in Chethan’s career, bringing greater attention to his action choreography beyond Kannada cinema.",
      "His association with the film developed through his earlier work on “Dhoomam”. During that project, he was introduced to actor Fahadh Faasil, who subsequently connected him with director Jithu Madhavan.",
      "This professional connection led to his involvement in “Aavesham”.",
      "The film’s energetic presentation and distinctive action sequences helped draw attention to the people responsible for bringing its physical performances to the screen.",
      "For Chethan, the project was an opportunity to apply his experience to a different filmmaking environment and collaborate with a new creative team.",
      "The recognition surrounding “Aavesham” also played a role in bringing his work to the attention of the team behind Jailer 2.",
    ],
  },
  {
    heading: "Chethan D’Souza in Jailer 2: Working with Rajinikanth",
    paragraphs: [
      "One of the most significant projects in Chethan D’Souza’s career is Jailer 2, the sequel to Rajinikanth’s successful film Jailer.",
      "Directed by Nelson Dilipkumar, the film features Rajinikanth in the lead role and has attracted considerable attention from audiences. Chethan’s involvement places his action design work within a major Tamil cinema production.",
      "According to Chethan’s press-meet account, the film’s team noticed his work on Aavesham and approached him to design the action for the Jailer 2 announcement teaser in January 2025. He was subsequently brought on board for the full film.",
      "Working on a production featuring a celebrated star such as Rajinikanth requires an understanding of the film’s tone, characterisation and visual style. Action sequences must fit the story and the performer while delivering the intended impact on screen.",
      "Chethan has explained that working with a legendary actor required him to adapt his approach. Earlier, he concentrated primarily on realistic, martial arts-based fights. For a large-scale commercial film, he needed to consider the character, the story and the overall scale of the action.",
    ],
  },
  {
    heading: "Beyond Jailer 2: Upcoming Films and Other Projects",
    paragraphs: [
      "Chethan’s professional portfolio extends beyond Tamil and Kannada cinema. His work includes projects across several Indian film industries, involving different performers, directors and approaches to action filmmaking.",
      "Chethan has trained actors including Fahadh Faasil, Karthi, Vijay Deverakonda, Sriimurali and Yuva Rajkumar for action-related roles and sequences.",
    ],
  },
  {
    heading: "CF Action Design",
    paragraphs: [
      "Beyond his work on individual films, Chethan has developed professional platforms for action design and stunt training.",
      "He is the founder and lead action designer of CF Action Design, which focuses on designing and preparing action sequences for film productions.",
      "Through CF Action Design, his team plans and tests sequences before filming. The company also operates a stunt rigging and safety truck supported by a dedicated rigging and safety crew.",
      "The preparation process can involve breaking a sequence into individual movements, assessing the requirements of each stunt, coordinating performers and equipment, and planning how the scene will be captured by the camera.",
      "Previsualisation and 3D animatics can further help the creative team review the sequence before the shoot.",
      "These processes are essential to balancing the director’s creative vision with the practical requirements of filming. They also demonstrate why professional action direction involves considerably more than choreographing a fight.",
    ],
  },
  {
    heading: "How Action Direction Is Evolving in Indian Cinema",
    paragraphs: [
      "Action filmmaking continues to develop through advances in camera technology, digital production, previsualisation, motion capture and stunt coordination.",
      "Modern action directors must understand physical movement while also working closely with directors, cinematographers, editors, performers and technical teams.",
      "Previsualisation allows sequences to be planned before filming, while 3D animatics can help communicate the intended action and camera movement. Motion capture creates additional possibilities for productions involving digital characters, animation and games.",
      "At the same time, the fundamentals remain important: character motivation, clear movement, timing, rehearsal and safety.",
      "Chethan’s experience across martial arts, parkour, gymnastics, stunt choreography and motion capture reflects the varied skills that can contribute to contemporary action filmmaking.",
    ],
  },
  {
    heading: "Conclusion",
    paragraphs: [
      "Chethan D’Souza’s journey from Udupi to major Indian film productions is a story of discipline, experimentation and professional growth. What began with a school karate class developed into a career that combines martial arts, stunt choreography, action design and filmmaking technology.",
      "His work through CF Action Design and Chaos Faktory further reflects his commitment to the technical preparation, training and coordination behind cinematic action.",
      "As audiences discover the action sequences in Jailer 2 and his other projects, Chethan’s journey offers a closer look at the professionals who help translate a director’s vision into compelling on-screen performances.",
    ],
  },
];

const FAQS = [
  {
    q: "Who is the action director of Jailer 2?",
    a: "Chethan D’Souza is credited as the action director and stunt choreographer for Jailer 2, starring Rajinikanth and directed by Nelson Dilipkumar.",
  },
  {
    q: "Where is Chethan D’Souza from?",
    a: "Chethan D’Souza was born near Udupi, Karnataka. He later moved to Bengaluru, where he worked as a software engineer before pursuing action direction full-time.",
  },
  {
    q: "Which movies has Chethan D’Souza worked on?",
    a: "His film credits include U Turn, Aavesham, Bagheera, UI, Max, 45 and Bhairathi Ranagal, along with other projects across Indian cinema.",
  },
  {
    q: "How did Chethan D’Souza enter the film industry?",
    a: "He shared his parkour short film Wrong Move with director Pawan Kumar, which led to an opportunity to choreograph fights for U Turn.",
  },
  {
    q: "What is CF Action Design?",
    a: "CF Action Design is Chethan D’Souza’s action design company. It works on stunt choreography, previsualisation, 3D animatics and motion capture.",
  },
];

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const existed = !!el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prevContent = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, existed, prevContent };
}

export default function JailerActionDirectorPost() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const origin = window.location.origin;
    const url = `${origin}${PAGE_PATH}`;
    const image = `${origin}/blog-image.png`;

    const metaRecords = [
      upsertMeta("name", "description", DESCRIPTION),
      upsertMeta("name", "robots", "index, follow"),
      upsertMeta("property", "og:type", "article"),
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", url),
      upsertMeta("property", "og:image", image),
      upsertMeta("name", "twitter:card", "summary_large_image"),
      upsertMeta("name", "twitter:title", TITLE),
      upsertMeta("name", "twitter:description", DESCRIPTION),
      upsertMeta("name", "twitter:image", image),
    ];

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalExisted = !!canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevHref = canonical.getAttribute("href");
    canonical.setAttribute("href", url);

    const ldJson = document.createElement("script");
    ldJson.type = "application/ld+json";
    ldJson.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: TITLE,
          description: DESCRIPTION,
          image,
          datePublished: PUBLISH_DATE,
          dateModified: PUBLISH_DATE,
          author: { "@type": "Person", name: "Chethan D’Souza" },
          publisher: {
            "@type": "Organization",
            name: "CF Action Design",
            logo: { "@type": "ImageObject", url: `${origin}/favicon-new.jpeg` },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ldJson);

    return () => {
      document.title = prevTitle;
      metaRecords.forEach(({ el, existed, prevContent }) => {
        if (!existed) el.remove();
        else if (prevContent !== null) el.setAttribute("content", prevContent);
      });
      if (!canonicalExisted) canonical.remove();
      else if (prevHref) canonical.setAttribute("href", prevHref);
      ldJson.remove();
    };
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Grain />
      <article className="px-4 md:px-10 pt-6 md:pt-10 pb-14 md:pb-24">
        <div className="mx-auto max-w-[1600px]">
          <div className="max-w-[1100px] mx-auto">
            <div className="relative w-full aspect-[16/9] overflow-hidden border border-border bg-[#0a0a0a]">
              <img
                src="/blog-image.png"
                alt="Chethan D'Souza, action director of Jailer 2, on set"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>

          <header className="max-w-[820px] mx-auto mt-10 md:mt-14">
            <span className="kicker">CF Action Design · Published Oct 15, 2026</span>
            <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight mt-4">
              {TITLE}
            </h1>
          </header>

          <div className="max-w-[820px] mx-auto mt-8 md:mt-10 space-y-5">
            <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
              Indian cinema’s most memorable action sequences are built on more than punches, stunts and spectacular visuals. They require martial arts expertise, creative thinking, detailed planning and an understanding of how action can move a story forward. One of the professionals bringing these elements together is Chethan D’Souza, the action director associated with Rajinikanth’s Jailer 2.
            </p>
            <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
              Born near Udupi in Karnataka, Chethan’s journey into filmmaking began with a school karate class and a passion for martial arts. He later explored parkour, gymnastics and movement arts while working as a software engineer in Bengaluru. What started as a personal interest eventually became a full-time career in action direction.
            </p>
          </div>

          <div className="max-w-[820px] mx-auto mt-10 md:mt-14">
            {SECTIONS.map((section) => (
              <section key={section.heading} className="mt-10 md:mt-14 first:mt-0">
                <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-4">
                  {section.heading}
                </h2>
                <div className="space-y-4">
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm sm:text-base leading-relaxed text-foreground/90">
                      {p}
                    </p>
                  ))}
                </div>
                {section.subheading && (
                  <p className="font-semibold text-sm sm:text-base mt-5 mb-2">{section.subheading}</p>
                )}
                {section.list && (
                  <p className="text-sm sm:text-base leading-relaxed text-foreground/90">
                    {section.list.join(", ")}
                  </p>
                )}
                {section.paragraphsAfterList && (
                  <div className="space-y-4 mt-4">
                    {section.paragraphsAfterList.map((p, i) => (
                      <p key={i} className="text-sm sm:text-base leading-relaxed text-foreground/90">
                        {p}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            ))}

            <div className="mt-10 md:mt-14">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono-c text-xs tracking-[0.15em] uppercase link-underline"
              >
                <Instagram size={16} /> Follow on Instagram
              </a>
            </div>

            {/* FAQ */}
            <section className="mt-16 md:mt-20">
              <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl leading-tight mb-6">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="border-t border-border">
                {FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-sm sm:text-base font-semibold hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>
        </div>
      </article>
    </div>
  );
}
